import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, Signal, SimpleChanges, computed, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore, SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable, StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { Promise as UnpatchedPromise } from '@rx-angular/cdk/zone-less/browser';
import { RxFor } from '@rx-angular/template/for';
import { BehaviorSubject, Subject, map, of, startWith, switchMap, take, tap } from 'rxjs';

import { SourceOfPick } from '../betslip-base/models/pick-models';
import { ShowInEpcotDirective } from '../common/epcot-config.directive';
import { EpcotConfigService } from '../common/epcot-config.service';
import { CorrectScoreOptions, CorrectScoreService } from './correct-score.service';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionComponent } from './option.component';

@Component({
    selector: 'ms-correct-score-option-group',
    templateUrl: './correct-score-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MarketHelpComponent, NgClass, RxFor, NgTemplateOutlet, ShowInEpcotDirective, OptionComponent, IconCustomComponent],
})
export class CorrectScoreOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() isShowMoreDisabled: boolean;
    @Input() showMarketHelp: boolean;
    @Input() crmPromotedOptionIdsMap: StringDictionary<number> = {};
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;

    private _isShowMoreDisabled = signal<boolean>(false);
    private correctScoreThreshold = this.marketGroupingConfig.showMoreCorrectScoreThreshold;
    private showMoreRowsThreshold = signal(this.correctScoreThreshold);

    optionsLayout = signal<CorrectScoreOptions>({
        layout: 1,
        options: [],
        rest: [],
    });

    scoreRowsCount: Signal<number> = computed(() => {
        return this.scoreOptions().length / this.optionsLayout().layout;
    });

    showAllOptions = computed(() => {
        const scoreRowsCount = this.optionsLayout().options.length / this.optionsLayout().layout;
        const isNotExceedingThreshold = scoreRowsCount + this.optionsLayout().rest.length <= this.showMoreRowsThreshold();

        return this._isShowMoreDisabled() || isNotExceedingThreshold;
    });

    // renderCallback
    readonly groupsRendered$ = new Subject<Nullable<EventOption>[]>();
    // strategy input
    readonly renderStrategy$ = new BehaviorSubject<string>(this.strategyProvider.primaryStrategy);

    private readonly scoreOptions = computed(() => {
        if (!this.isCollapsed() || this.showAllOptions()) {
            return this.optionsLayout().options;
        }
        const threshold = this.showMoreRowsThreshold() * this.optionsLayout().layout;

        return this.optionsLayout().options.slice(0, threshold);
    });

    scoreOptions$ = toObservable(this.scoreOptions).pipe(
        switchMap((options) => {
            if (this.startFresh) {
                this.startFresh = false;
                UnpatchedPromise.resolve().then(() => this.renderStrategy$.next('native'));

                return this.groupsRendered$.pipe(
                    take(1),
                    map(() => options),
                    tap(() => this.renderStrategy$.next(this.strategyProvider.primaryStrategy)),
                    startWith([]),
                );
            }

            return of(options);
        }),
    );

    restOptions$ = toObservable(
        computed(() => {
            if (!this.isCollapsed() || this.showAllOptions()) {
                return this.optionsLayout().rest;
            }
            const threshold = this.showMoreRowsThreshold() - this.scoreRowsCount();

            return this.optionsLayout().rest.slice(0, threshold);
        }),
    );

    scoreOptionsClass = computed(() => (this.optionsLayout().layout === 2 ? 'double' : 'triple'));

    isCollapsed = signal(true);

    private sitecoreReady = toSignal(this.sitecoreEventDetails.whenReady.pipe(map(() => true)), { initialValue: false });

    headerNames = computed(() => {
        if (!this.sitecoreReady()) {
            return [];
        }
        const headers = ['1', '2'];

        if (this.optionsLayout().layout === 3) {
            if (this.epcotConfigService.isEnabled()) {
                headers.splice(1, 0, this.sitecoreEventDetails.eventDetails.Tie);
            } else {
                headers.splice(1, 0, 'X');
            }
        }

        return headers;
    });

    private startFresh = false;

    constructor(
        public sitecore: Sitecore,
        public sitecoreEventDetails: SitecoreEventDetailsConfig,
        private correctScoreService: CorrectScoreService,
        private trackingService: TrackingService,
        private epcotConfigService: EpcotConfigService,
        private strategyProvider: RxStrategyProvider,
        private marketGroupingConfig: MarketGroupingConfig,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.optionGroup) {
            if (!changes.optionGroup.firstChange && changes.optionGroup.previousValue?.id !== this.optionGroup.id) {
                this.startFresh = true;
            }
            this.optionsLayout.set(this.correctScoreService.getCorrectScoreOptions(this.optionGroup));
        }

        if (changes.optionsLimitOverride) {
            this.showMoreRowsThreshold.set(this.optionsLimitOverride ?? this.correctScoreThreshold);
        }

        if (changes.isShowMoreDisabled) {
            this._isShowMoreDisabled.set(this.isShowMoreDisabled);
        }
    }

    expand(): void {
        this.isCollapsed.set(!this.isCollapsed());

        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Visible Selections',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.isCollapsed() ? 'Show More' : 'Show Less',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        });
    }

    trackOption(index: number, option: Nullable<EventOption>): string {
        return option ? `${option.id}` : `_${index}`;
    }

    isPlaceholderShadowVisible(index: number): boolean {
        return index >= this.optionsLayout().options.length - 2;
    }
}
